import React from 'react';
import { Users, CheckCircle2, XCircle, Calendar, Clock, User, Phone } from 'lucide-react';
import { formatBookingId, format24HourTime } from '../../../context/DashboardContext';

const BookingTable = ({ filteredBookings = [], activeTab, onStatusChange, formatDateTime }) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {filteredBookings.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Order ID</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Details</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date & 24h Time</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Final Bill</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Your Share</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredBookings.map((booking) => {
                const customer = booking.booking_id || booking.user_id || {};
                const finalBill = booking.final_bill_amount || booking.advance_Amt || 0;
                const shareAmount = booking.partnerShare ?? 0;
                const isUnconfirmed = !['confirmed', 'completed'].includes((booking.status || '').toLowerCase());

                // Strictly format Booking ID: 5-char lowercase (a-z, 0-9)
                const formattedId = formatBookingId(customer.booking_id || booking._id);

                // Strictly format Time: 24-hour format (HH:mm)
                const time24 = format24HourTime(booking.bookedTime || booking.time || booking.date);

                const guestName = customer.name || 'Customer';
                const guestPhone = customer.phone_number || 'N/A';

                return (
                  <tr key={booking._id} className="hover:bg-slate-50/80 transition-colors text-xs group">
                    <td className="py-3.5 px-4 font-mono font-bold text-slate-600 lowercase">
                      {formattedId}
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-teal-50 text-[#067978] flex items-center justify-center font-bold text-[10px] shrink-0">
                          <User size={13} />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-extrabold text-slate-800 text-xs">{guestName}</span>
                          <span className="text-[10px] text-slate-400 font-semibold flex items-center gap-1">
                            <Phone size={9} />
                            {guestPhone}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-1.5 text-slate-700 font-bold">
                        <Users size={13} className="text-[#067978]" />
                        <span>{booking.totalMembers || 1} Persons</span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-700">{new Date(booking.date).toLocaleDateString()}</span>
                        <span className="text-[10px] font-bold text-[#067978] flex items-center gap-1 mt-0.5">
                          <Clock size={10} />
                          {time24}
                        </span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-black text-slate-800 text-xs">
                      ₹{Number(finalBill).toLocaleString('en-IN')}
                    </td>

                    <td className="py-3.5 px-4 font-black text-emerald-600 text-xs">
                      ₹{Number(shareAmount).toLocaleString('en-IN')}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                        booking.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                        booking.status === 'Pending' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        booking.status === 'Completed' ? 'bg-[#067978]/10 text-[#067978] border border-[#067978]/20' :
                        'bg-rose-50 text-rose-600 border border-rose-100'
                      }`}>
                        {booking.status}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      {isUnconfirmed ? (
                        <div className="flex justify-end items-center gap-1.5">
                          <button
                            onClick={() => onStatusChange(booking._id, 'Confirmed')}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold rounded-lg shadow-sm transition-all flex items-center gap-1 active:scale-95"
                          >
                            <CheckCircle2 size={12} />
                            Confirm
                          </button>
                          <button
                            onClick={() => onStatusChange(booking._id, 'Cancelled')}
                            className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 text-[10px] font-extrabold rounded-lg transition-all flex items-center gap-1 active:scale-95"
                          >
                            <XCircle size={12} />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-400 font-bold">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
          <div className="p-3 bg-slate-50 rounded-2xl text-slate-300 mb-3">
            <Calendar size={24} />
          </div>
          <h3 className="text-sm font-black text-slate-700">No orders found</h3>
          <p className="text-slate-400 text-xs font-medium max-w-xs mt-0.5">Try adjusting your filters or search query.</p>
        </div>
      )}
    </div>
  );
};

export default BookingTable;
